import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  SportsSoccer as SportsIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setFilters, fetchMissions } from '../redux/slices/missionsSlice';
import { useNavigate } from 'react-router-dom';
import './SearchBar.css';

const SearchBar = ({ variant = 'hero' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [discipline, setDiscipline] = useState('');
  const [location, setLocation] = useState('');

  const disciplines = [
    // Encadrement
    'Entraîneur sportif',
    'Coach sportif',
    'Préparateur physique',
    'Arbitre',
    'Juge',
    'Formateur',
    // Santé et bien-être
    'Kinésithérapeute du sport',
    'Ostéopathe',
    'Nutritionniste sportif',
    'Psychologue du sport',
    // Événementiel et logistique
    'Organisateur d\'événements sportifs',
    'Personnel d\'accueil',
    'Personnel de sécurité',
    'Logisticien',
    // Communication et marketing
    'Community manager sport',
    'Photographe sportif',
    'Vidéaste sportif',
    'Journaliste sportif',
    // Disciplines spécifiques
    'Football',
    'Basketball',
    'Tennis',
    'Natation',
    'Volleyball',
    'Handball',
    'Rugby',
    'Athlétisme',
    'Gymnastique',
  ];

  const locations = [
    'Paris',
    'Lyon',
    'Marseille',
    'Toulouse',
    'Nice',
    'Nantes',
    'Strasbourg',
    'Montpellier',
    'Bordeaux',
    'Lille',
    'Rennes',
    'Reims',
  ];

  const handleSearch = () => {
    dispatch(setFilters({ discipline, location }));
    dispatch(fetchMissions());
    navigate('/missions');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  if (variant === 'compact') {
    return (
      <Paper
        className="search-bar search-bar--compact"
        elevation={2}
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: '25px',
          backgroundColor: 'background.paper',
        }}
      >
        <TextField
          placeholder="Spécialité"
          value={discipline}
          onChange={(e) => setDiscipline(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              border: 'none',
              '& fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SportsIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          placeholder="Localisation"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          onKeyPress={handleKeyPress}
          size="small"
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              border: 'none',
              '& fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          sx={{
            borderRadius: '20px',
            minWidth: 'auto',
            px: 2,
          }}
        >
          <SearchIcon />
        </Button>
      </Paper>
    );
  }

  return (
    <Box className="search-bar search-bar--hero">
      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 2 : 3,
          borderRadius: '20px',
          backgroundColor: 'background.paper',
          maxWidth: '800px',
          margin: '0 auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0,
            alignItems: 'stretch',
            border: '2px solid #e0e0e0',
            borderRadius: '25px',
            overflow: 'hidden',
            backgroundColor: 'white',
          }}
        >
          {/* Discipline Field */}
          <Autocomplete
            freeSolo
            options={disciplines}
            value={discipline}
            onInputChange={(event, newValue) => {
              setDiscipline(newValue || '');
            }}
            sx={{
              flex: isMobile ? 1 : 1.2,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Spécialité ou domaine"
                onKeyPress={handleKeyPress}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: isMobile ? '20px' : '0',
                    border: 'none',
                    backgroundColor: 'transparent',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                    minHeight: '56px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '1rem',
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SportsIcon sx={{ color: '#666', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Divider */}
          {!isMobile && (
            <Box
              sx={{
                width: '1px',
                backgroundColor: '#e0e0e0',
                alignSelf: 'stretch',
                my: 1,
              }}
            />
          )}

          {/* Location Field */}
          <Autocomplete
            freeSolo
            options={locations}
            value={location}
            onInputChange={(event, newValue) => {
              setLocation(newValue || '');
            }}
            sx={{
              flex: isMobile ? 1 : 1.5,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Localisation (ville, région...)"
                onKeyPress={handleKeyPress}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: isMobile ? '20px' : '0',
                    border: 'none',
                    backgroundColor: 'transparent',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                    minHeight: '56px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '1rem',
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon sx={{ color: '#666', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Search Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            size="large"
            sx={{
              borderRadius: isMobile ? '20px' : '0 20px 20px 0',
              px: isMobile ? 3 : 4,
              py: isMobile ? 1.5 : 'auto',
              minHeight: '56px',
              minWidth: isMobile ? 'auto' : '140px',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: 'none',
              background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
              '&:hover': {
                boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.3s ease',
            }}
            startIcon={<SearchIcon />}
          >
            Rechercher
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SearchBar;